import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Truck, PackageCheck } from "lucide-react";

const FAQS = [
    {
        q: "How long does the battery last?",
        a: "The 3600mAh battery delivers up to 12 hours of use on lower speeds. Higher speeds use more power — the digital display helps you pace it. Recharging is via the included Type-C cable.",
    },
    {
        q: "How does the IcePlate cooling work?",
        a: "IcePlate editions have a metal cold-compress plate at the center of the fan. It chills in about 3 seconds — press it against your wrist, neck or forehead while the fan runs for a dual cooling effect. Note: the cooling mode pauses while the fan is charging.",
    },
    {
        q: "What's in the box?",
        a: "Your TurboFan Pro, a Type-C charging cable and a lanyard so you can wear it around your neck. The folding handle also lets it stand on a desk.",
    },
    {
        q: "Is it loud?",
        a: "It's designed for everyday environments — desks, libraries, bedrooms. Lower speeds are whisper-soft; higher speeds trade a little sound for a lot of airflow. With 100 levels, you choose the balance.",
    },
    {
        q: "How fast is shipping?",
        a: "Orders are dispatched within 24–48 hours and you'll receive a tracking link by email as soon as your fan is on its way. Need it sooner? Add the Fast Shipping + Protection Bundle at checkout for $4.99 — your order jumps the queue with priority dispatch, and it's covered with an instant replacement if it's ever lost or damaged in transit.",
    },
    {
        q: "What if my fan arrives damaged?",
        a: "Contact us with your order number and a photo — we'll arrange a replacement or refund for any fan that arrives damaged or faulty. We're a small team and we answer every message.",
    },
];

export default function FAQ() {
    return (
        <section id="faq" data-testid="faq-section" className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32">
            <div className="grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
                        <p className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-3">Questions</p>
                        <h2 className="font-display uppercase font-700 tracking-tighter text-4xl md:text-6xl leading-[0.95]">
                            Before<br />you <span className="text-cyan-300">ask.</span>
                        </h2>
                        <div className="mt-10 space-y-4">
                            <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="shipping-info-card">
                                <Truck className="w-5 h-5 text-cyan-300 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold">Shipping</p>
                                    <p className="text-xs text-zinc-500 leading-relaxed mt-1">Dispatched in 24–48h with tracked delivery. Tracking link sent to your email automatically.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="returns-info-card">
                                <PackageCheck className="w-5 h-5 text-cyan-300 mt-0.5 shrink-0" />
                                <div>
                                    <p className="text-sm font-semibold">Damaged or faulty?</p>
                                    <p className="text-xs text-zinc-500 leading-relaxed mt-1">We'll replace or refund it — just send us your order number and a photo.</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="lg:col-span-7"
                >
                    <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
                        {FAQS.map((f, i) => (
                            <AccordionItem key={f.q} value={`item-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 data-[state=open]:border-cyan-300/40 transition-colors">
                                <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-display uppercase tracking-wide text-sm md:text-base py-5 hover:no-underline hover:text-cyan-300">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent data-testid={`faq-answer-${i}`} className="text-sm text-zinc-400 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </motion.div>
            </div>
        </section>
    );
}
